import { useMemo } from 'react';

import {
    useGetEquipmentsQuery,
    useGetSuppliersQuery,
    useGetTotemsWithEquipmentsQuery,
    TotadminPermission,
} from 'data/__generated__';
import { useHasPermission } from 'hooks/useHasPermission';

import { DataTable } from 'components/DataTable';
import { Loader } from 'components/Loader';

import { getEquipmentsColumns, type EquipmentRow } from '../constants/equipmentsColumns';

export const EquipmentsList = () => {
    const { data: equipmentData, loading: equipmentLoading, error: equipmentError } = useGetEquipmentsQuery();
    const { loading: suppliersLoading, data: suppliersData, error: suppliersError } = useGetSuppliersQuery();
    // Les totems sont une ressource à part : sans le droit, la requête ne part pas et la colonne « Totems ayant
    // l'équipement » reste vide — sinon « Accès refusé » ferait tomber toute la liste.
    const canReadTotems = useHasPermission(TotadminPermission.TotemsRead);
    const {
        loading: totemsWithEquipmentsLoading,
        data: totemsWithEquipmentsData,
        error: totemsWithEquipmentsError,
    } = useGetTotemsWithEquipmentsQuery({ skip: !canReadTotems });

    const columns = useMemo(
        () =>
            getEquipmentsColumns(suppliersData?.suppliers ?? [], totemsWithEquipmentsData?.totemsWithEquipments ?? []),
        [suppliersData, totemsWithEquipmentsData],
    );

    if (equipmentLoading || suppliersLoading || totemsWithEquipmentsLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (
        equipmentError ||
        !equipmentData ||
        suppliersError ||
        !suppliersData ||
        totemsWithEquipmentsError ||
        (canReadTotems && !totemsWithEquipmentsData)
    ) {
        throw new Error('Une erreur est survenue lors de la récupération des équipements');
    }

    const { adminEquipments: equipments } = equipmentData;

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<EquipmentRow>
                columns={columns}
                data={equipments}
                filterMode="client"
                getRowId={(row) => row._id}
                getRowLink={(row) => `/equipment/${row._id}`}
            />
        </div>
    );
};
